import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { COMPANY } from "../data/products";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/about", label: "About Us", testid: "nav-link-about" },
  { to: "/products", label: "Products", testid: "nav-link-products" },
  { to: "/gallery", label: "Gallery", testid: "nav-link-gallery" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-slate-800/80 bg-obsidian/85 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
          <img src="/logos/sohom-blue-white.png" alt="SOHOM" className="h-8 w-auto" />
          <span className="hidden border-l border-slate-700 pl-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 lg:inline">
            Architectural Systems
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `group relative text-sm font-medium transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-slate-400 hover:text-slate-100"
                }`
              }
            >
              {l.label}
              <span
                className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${
                  location.pathname === l.to ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-quote-cta"
            className="group flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-obsidian transition-colors duration-300 hover:bg-gold-hover"
          >
            Get Instant Quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          className="text-slate-200 lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-slate-800/80 bg-obsidian/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`${l.testid}-mobile`}
                  className={({ isActive }) =>
                    `rounded-lg px-4 py-3 font-heading text-lg font-semibold transition-colors ${
                      isActive ? "bg-gold/10 text-gold" : "text-slate-300 hover:bg-surface"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="nav-quote-cta-mobile"
                className="mt-3 rounded-full bg-gold px-5 py-3 text-center font-semibold text-obsidian"
              >
                Get Instant Quote
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
